import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore }    from '../store/authStore.js';
import { usePrinterStore } from '../store/printerStore.js';
import { thermalPrinter }  from '../native/thermalPrinter.js';
import { getBusinessInfo, sendReceiptByEmail } from '../api/pos.api.js';
import FacturaModal from '../components/FacturaModal.jsx';
import { IcoCheck, IcoNote, IcoPrint, IcoEmail } from '../components/common/Icons.jsx';
import styles from './ReceiptScreen.module.css';

const fmt    = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const fmtNow = ()  => new Date().toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });

const METHOD_LABEL = {
  cash:     'Efectivo',
  card:     'Tarjeta',
  transfer: 'Transferencia',
  mixed:    'Mixto',
};

export default function ReceiptScreen() {
  const navigate     = useNavigate();
  const { state }    = useLocation();
  const user         = useAuthStore((s) => s.user);

  const { total = 0, subtotal = 0, discount = 0, method = 'cash', changeDue = 0, client_uuid = '', paymentId = null, cfdiItems = [], note = '', customer = null } = state ?? {};

  const [showFactura,  setShowFactura]  = useState(false);
  const [showEmail,    setShowEmail]    = useState(false);
  const [email,        setEmail]        = useState('');
  const [sending,      setSending]      = useState(false);
  const [sent,         setSent]         = useState(false);
  const [sendError,    setSendError]    = useState(null);
  const [business,     setBusiness]     = useState(null);
  const [printTime]                     = useState(fmtNow);
  const [autoPrint,    setAutoPrint]    = useState(() => localStorage.getItem('pos_auto_print') === 'true');
  const [btPrinting,   setBtPrinting]   = useState(false);
  const [btError,      setBtError]      = useState(null);
  const [bizReady,     setBizReady]     = useState(false);
  const printedRef                      = useRef(false);
  const printerName  = usePrinterStore((s) => s.deviceName);
  const printerDevId = usePrinterStore((s) => s.deviceId);
  const printerTrans = usePrinterStore((s) => s.transport);

  const toggleAutoPrint = () => {
    const next = !autoPrint;
    setAutoPrint(next);
    localStorage.setItem('pos_auto_print', String(next));
  };

  useEffect(() => {
    getBusinessInfo()
      .then(setBusiness)
      .catch(() => {})
      .finally(() => setBizReady(true));
  }, []);

  // Imprime una sola vez, y solo cuando los datos del negocio ya se
  // resolvieron. Antes el efecto dependía de `business`: si la petición
  // tardaba más de 600ms el primer timer ya había disparado (ticket sin
  // encabezado) y el cambio de `business` programaba un segundo — de ahí
  // los dos tickets por venta.
  useEffect(() => {
    if (!autoPrint || !bizReady || printedRef.current) return;
    const t = setTimeout(() => {
      printedRef.current = true;
      handleBtPrint();
    }, 300);
    return () => clearTimeout(t);
  }, [autoPrint, bizReady]);

  const handleBtPrint = async () => {
    setBtError(null);
    if (printerDevId && !thermalPrinter.isConnected()) {
      // autoReconnect enruta por transporte (USB o BLE); si falla,
      // printReceipt cae solo al window.print()
      await thermalPrinter.autoReconnect(printerDevId, printerTrans).catch(() => {});
    }
    if (!thermalPrinter.isConnected()) {
      window.print();
      return;
    }
    setBtPrinting(true);
    try {
      await thermalPrinter.printReceipt({
        business,
        date:      printTime,
        cashier:   user?.name ?? user?.email ?? null,
        customer:  customer?.name ?? null,
        folio:     client_uuid.slice(-8).toUpperCase(),
        items:     cfdiItems,
        subtotal,
        discount,
        total,
        method,
        changeDue,
        note:      note || null,
      });
    } catch (err) {
      setBtError('Error al imprimir: ' + (err.message ?? 'Intenta de nuevo'));
    } finally {
      setBtPrinting(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setSendError(null);
    try {
      await sendReceiptByEmail(paymentId, { email: email.trim(), items: cfdiItems });
      setSent(true);
      setShowEmail(false);
    } catch (err) {
      setSendError(err.response?.data?.error ?? 'Error al enviar el correo');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.root}>

      {/* ── Ticket térmico (oculto en pantalla, visible al imprimir) ── */}
      <div className={styles.ticket}>
        <div className={styles.ticketHeader}>
          <p className={styles.storeName}>{business?.name ?? 'Geek Commerce'}</p>
          {business?.rfc && <p className={styles.ticketMeta}>RFC: {business.rfc}</p>}
          {business?.regimen_fiscal && <p className={styles.ticketMeta}>Régimen: {business.regimen_fiscal}</p>}
          {business?.address && <p className={styles.ticketMeta}>{business.address}</p>}
          {business?.phone && <p className={styles.ticketMeta}>Tel: {business.phone}</p>}
        </div>

        <div className={styles.ticketDivider} />

        <div className={styles.ticketMeta2}>
          <p>{printTime}</p>
          {user?.name && <p>Cajero: {user.name}</p>}
          <p>Folio: {client_uuid.slice(-8).toUpperCase()}</p>
          {customer?.name && <p>Cliente: {customer.name}</p>}
        </div>

        <div className={styles.ticketDivider} />

        {cfdiItems.length > 0 && (
          <div className={styles.ticketItems}>
            {cfdiItems.map((item, i) => (
              <div key={i} className={styles.ticketItem}>
                <div className={styles.ticketItemName}>
                  {item.quantity > 1 && <span className={styles.qty}>{item.quantity}×</span>}
                  {item.name}
                  {item.original_price && (
                    <span className={styles.ticketOrigPrice}> ({fmt(item.original_price)})</span>
                  )}
                </div>
                <span className={styles.ticketItemPrice}>{fmt(item.quantity * item.unit_price)}</span>
              </div>
            ))}
          </div>
        )}

        <div className={styles.ticketDivider} />

        {discount > 0 && (
          <>
            <div className={styles.ticketRow}>
              <span>Subtotal</span>
              <span>{fmt(subtotal)}</span>
            </div>
            <div className={styles.ticketRow}>
              <span>Descuento</span>
              <span>-{fmt(discount)}</span>
            </div>
          </>
        )}
        <div className={styles.ticketTotal}>
          <span>TOTAL</span>
          <strong>{fmt(total)}</strong>
        </div>
        <div className={styles.ticketRow}>
          <span>Forma de pago</span>
          <span>{METHOD_LABEL[method] ?? method}</span>
        </div>
        {method === 'cash' && changeDue > 0 && (
          <div className={styles.ticketRow}>
            <span>Cambio</span>
            <span>{fmt(changeDue)}</span>
          </div>
        )}

        {note && (
          <div className={styles.ticketNote}>
            <p>Nota: {note}</p>
          </div>
        )}

        <div className={styles.ticketFooter}>
          <p>Este comprobante no es un CFDI.</p>
          <p>Solicita tu factura al establecimiento.</p>
          <p>¡Gracias por su compra!</p>
        </div>
      </div>

      {/* ── Card de acciones (solo pantalla) ── */}
      <div className={styles.card}>
        <div className={styles.checkmark}><IcoCheck size={28} /></div>
        <h1 className={styles.title}>¡Cobro exitoso!</h1>
        <p className={styles.amount}>{fmt(total)}</p>
        <p className={styles.method}>{METHOD_LABEL[method] ?? method}</p>

        {method === 'cash' && changeDue > 0 && (
          <div className={styles.change}>
            <span>Cambio</span>
            <strong>{fmt(changeDue)}</strong>
          </div>
        )}

        <p className={styles.ref}>
          Folio: <span className={styles.uuid}>{client_uuid.slice(-8).toUpperCase()}</span>
        </p>

        {note && (
          <p className={styles.noteTag}><IcoNote size={13} /> {note}</p>
        )}

        {/* Formulario de email */}
        {showEmail ? (
          <form onSubmit={handleSendEmail} className={styles.emailForm}>
            <input
              className={`input ${styles.emailInput}`}
              type="email"
              placeholder="correo@ejemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
            {sendError && <p className={styles.sendError}>{sendError}</p>}
            <div className={styles.emailActions}>
              <button type="button" className="btn btn--ghost" onClick={() => { setShowEmail(false); setSendError(null); }}>
                Cancelar
              </button>
              <button type="submit" className="btn btn--primary" disabled={sending}>
                {sending ? <span className="spinner" /> : 'Enviar'}
              </button>
            </div>
          </form>
        ) : (
          <div className={styles.actions}>
            <button className="btn btn--primary btn--full" onClick={() => navigate('/', { replace: true })}>
              Nueva venta
            </button>
            {printerDevId ? (
              <button
                className="btn btn--secondary btn--full"
                onClick={handleBtPrint}
                disabled={btPrinting}
              >
                {btPrinting ? <span className="spinner" /> : <><IcoPrint size={14} /> {printerName ?? 'Impresora BT'}</>}
              </button>
            ) : (
              <button className="btn btn--secondary btn--full" onClick={() => window.print()}>
                Imprimir ticket
              </button>
            )}
            {btError && <p style={{ fontSize: 12, color: 'var(--negative)', margin: '0' }}>{btError}</p>}
            <button
              className={`btn btn--full ${autoPrint ? 'btn--secondary' : 'btn--ghost'}`}
              onClick={toggleAutoPrint}
            >
              {autoPrint ? <><IcoCheck size={13} /> Auto-imprimir activado</> : 'Auto-imprimir al cobrar'}
            </button>
            <button
              className={`btn btn--full ${sent ? 'btn--ghost' : 'btn--secondary'}`}
              onClick={() => { setSent(false); setShowEmail(true); }}
            >
              {sent ? <><IcoCheck size={13} /> Correo enviado · Reenviar</> : <><IcoEmail size={14} /> Enviar por correo</>}
            </button>
            <button className="btn btn--ghost btn--full" onClick={() => setShowFactura(true)}>
              Solicitar factura
            </button>
          </div>
        )}
      </div>

      {showFactura && (
        <FacturaModal
          onClose={() => setShowFactura(false)}
          items={cfdiItems}
          formaPago={method}
          posPaymentId={paymentId}
        />
      )}
    </div>
  );
}
